import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
    InputBase,
    Button as MUIButton,
    ListItemButton,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, Button } from '@/components';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';

import { CustomOptionItem, CustomOptionsInputModalProps } from './types';
import { getValidationSchema } from './validation';

export const CustomOptionsInputModal = ({
    isOpen,
    onClose,
    onChange,
    options,
}: CustomOptionsInputModalProps) => {
    const { t } = useTranslation();
    const [selectedOption, setSelectedOption] = useState<CustomOptionItem>(
        options[0],
    );

    const {
        errors,
        touched,
        handleBlur,
        handleChange,
        values,
        submitForm,
        setFieldValue,
    } = useFormik({
        initialValues: {
            amount: selectedOption.inputValue,
        },
        validationSchema: getValidationSchema(t),
        onSubmit: (values) => {
            onChange({ ...selectedOption, inputValue: values.amount });
        },
    });

    const handleOptionSelection = (option: CustomOptionItem) => {
        setSelectedOption(option);
    };

    const handleIncrease = () => {
        setFieldValue('amount', Number(values.amount) + 1);
    };

    const handleDecrease = () => {
        if (Number(values.amount) > 0) {
            setFieldValue('amount', Number(values.amount) - 1);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            center
            disableRestoreFocus
            testID="AddEditScheduleModal"
            styles="py-0 relative  h-[380px] w-[420px] p-[20px]"
        >
            <Typography fontWeight="bold" sx={{ marginBottom: 1.5 }}>
                {t('ShowEvent:custom_reminder')}
            </Typography>
            <div className="h-[60px] w-full">
                <div className="flex flex-row border-[1px] items-center justify-between border-regentGray rounded-md px-[15px] py-[7.5px]">
                    <InputBase
                        fullWidth
                        id="amount"
                        data-test-id="AddEditScheduleForm-Amount"
                        error={!!errors.amount && !!touched.amount}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        name="amount"
                        value={values.amount}
                        size="small"
                        className="w-[200px] p-[0px]"
                        sx={{
                            '& .MuiInputBase-input': {
                                padding: 0,
                            },
                        }}
                    />
                    <div className="flex flex-row align-center gap-2">
                        <MUIButton
                            size="small"
                            onClick={handleIncrease}
                            className="w-[20px] min-w-[20px] h-[20px]"
                        >
                            <AddCircleOutlineIcon
                                htmlColor={ENTITY_COLOR.schedule}
                            />
                        </MUIButton>
                        <MUIButton
                            size="small"
                            onClick={handleDecrease}
                            className="w-[20px] min-w-[20px] h-[20px]"
                        >
                            <RemoveCircleOutlineIcon
                                htmlColor={ENTITY_COLOR.schedule}
                            />
                        </MUIButton>
                    </div>
                </div>
                <div className="pl-[15px]">
                    <Typography variant="caption" color="error">
                        {errors.amount}
                    </Typography>
                </div>
            </div>

            {options.map((option, index) => (
                <div
                    key={option.value}
                    className={
                        index === options.length - 1
                            ? ''
                            : 'border-b border-gray-200'
                    }
                >
                    <ListItemButton
                        onClick={() => handleOptionSelection(option)}
                        sx={{
                            paddingTop: 1.5,
                            paddingBottom: 1.5,
                            pr: 1,
                            pl: 1,
                        }}
                    >
                        <div className="flex flex-row justify-between w-full">
                            <Typography>{option.text}</Typography>
                            {option.value === selectedOption.value && (
                                <CheckIcon fontSize="small" />
                            )}
                        </div>
                    </ListItemButton>
                </div>
            ))}
            <div className="flex justify-end mt-[10px] gap-[10px]">
                <Button caption={t('Actions:close')} onClick={onClose} />
                <Button
                    caption={t('Actions:save_changes')}
                    onClick={submitForm}
                    variant="contained"
                    sx={{
                        backgroundColor: ENTITY_COLOR.schedule,
                        color: COLOR.white,
                        '&:hover': {
                            color: COLOR.white,
                            backgroundColor: ENTITY_COLOR.schedule,
                        },
                    }}
                />
            </div>
        </Modal>
    );
};
